"use client";

import { useRef } from "react";
import type { ReactNode } from "react";
import { ACCENT, Bubble, Desk, ENGINES, MONO, TXT, QUESTIONS, Rep, Tag, useScrollTime } from "@/app/home/office/parts";
import type { Look, Pt } from "@/app/home/office/parts";
import { AUDIT_LINES, BRAND, BRAND_LINES, EXEC, HUMAN_LOOK, LEAD_LOOK, MONITOR, MONITOR_LINES, SITE_AUDIT, WALKERS } from "@/app/home/AgentFloor";

// Small, non-interactive scenes that tell the homepage story between sections,
// with the same cast as the agent office. Scroll drives each scene: it plays its
// steps from 0 to `end` while it moves up through the viewport, and ends on its payoff.

const W = 800;
const H = 200;
const FAINT = "#83858B";
const NEGATIVE = "#D64B3B";
const GOOD = "#16A34A";

const BUYER_LOOK: Look = { shirt: "#F97316", pants: "#334155", skin: "#F3D2B3", hair: "#2B1D14", style: "side" };
const RIVAL_LOOK: Look = { shirt: "#64748B", pants: "#334155", skin: "#E0A47A", hair: "#3B2A20", style: "spiky" };
const QA_LOOK = WALKERS[6].look;
const CONTENT_LOOK = WALKERS[4].look;

const clamp01 = (k: number) => Math.max(0, Math.min(1, k));
const cycle = (t: number, period: number) => ({ n: Math.floor(t / period), ph: ((t % period) + period) % period });
const lerp = (a: number, b: number, k: number) => a + (b - a) * k;

// `spot` (viewBox x) is a free place on the floor where the page guide stops; `say` is what it says there.
function Scene({ step, title, label, end, still, spot, say, from = 0.9, span = 0.7, children }: { step: string; title: string; label: string; end: number; still: number; spot: number; say: string; from?: number; span?: number; children: (t: number) => ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  // 0 when the scene's top reaches `from` of the viewport height, 1 after a further `span` of scroll (defaults: 90% → 20%).
  const t = useScrollTime(
    () => {
      const el = ref.current;
      if (!el) return 0;
      const vh = window.innerHeight;
      return (vh * from - el.getBoundingClientRect().top) / (vh * span);
    },
    0,
    end,
    still,
  );
  return (
    <div ref={ref} data-story-step={step} data-guide={title} data-guide-spot={spot} data-guide-say={say} className="w-full py-8 sm:py-10">
      <div className="max-w-[920px] mx-auto px-4 md:px-8">
        <p className="text-center font-mono text-[11px] font-bold tracking-widest uppercase text-ink-faint mb-3">
          <span className="text-accent">{step}</span> · {title}
        </p>
        <div className="overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <svg viewBox={`0 0 ${W} ${H}`} className="block w-full min-w-[600px] h-auto pointer-events-none select-none" role="img" aria-label={label}>
            <rect x={30} y={H - 48} width={W - 60} height={30} rx={15} fill="var(--il-wall)" opacity={0.7} />
            {children(t)}
          </svg>
        </div>
      </div>
    </div>
  );
}

function Flow({ d, t }: { d: string; t: number }) {
  return <path d={d} fill="none" stroke={ACCENT} strokeWidth={2} strokeDasharray="5 6" strokeDashoffset={-t * 40} opacity={0.6} />;
}

function Paper({ at, check = false }: { at: Pt; check?: boolean }) {
  const [x, y] = at;
  return (
    <g>
      <rect x={x - 7} y={y - 6} width={14} height={11} rx={1.5} fill="var(--il-panel)" stroke="var(--il-mute)" />
      <line x1={x - 4} y1={y - 2} x2={x + 4} y2={y - 2} stroke="var(--il-mute)" />
      {check && <circle cx={x + 6} cy={y - 6} r={4.5} fill="#22C55E" />}
    </g>
  );
}

// A seated agent: the person first, then the desk in front, then the tag on the desk front.
function Seated({ x, look, t, seed, tag, glow = true }: { x: number; look: Look; t: number; seed: number; tag: string; glow?: boolean }) {
  return (
    <g>
      <Rep at={[x, 148]} l={look} t={t} seed={seed} typing />
      <Desk x={x} sy={140} glow={glow} />
      <Tag x={x} y={153} text={tag} />
    </g>
  );
}

const Text = ({ x, y, children, size = 10, fill = TXT, anchor = "start", weight = 700 }: { x: number; y: number; children: ReactNode; size?: number; fill?: string; anchor?: "start" | "middle" | "end"; weight?: number }) => (
  <text x={x} y={y} fontFamily={MONO} fontSize={size} fontWeight={weight} fill={fill} textAnchor={anchor}>
    {children}
  </text>
);

/* 01 — the buyer asks AI for a shortlist, and you are not on it */
export function StoryBuyerAsks() {
  return (
    <Scene step="01" spot={335} say="they skipped you!" end={8.9} title="Buyer asks AI" still={6.5} label="A buyer asks an AI engine for a supplier shortlist; three competitors are cited and your brand is not listed">
      {(t) => {
        const { n, ph } = cycle(t, 9);
        const q = QUESTIONS[n % QUESTIONS.length];
        const eng = ENGINES[n % ENGINES.length];
        const typed = q.slice(0, Math.round(clamp01((ph - 0.6) / 2.2) * q.length));
        const answers = ph > 3.4 ? Math.min(3, Math.floor((ph - 3.4) / 0.5) + 1) : 0;
        const missing = ph > 5.2;
        return (
          <g>
            <Flow d="M248 120 C 300 120, 320 100, 378 100" t={t} />
            <Seated x={200} look={BUYER_LOOK} t={t} seed={1} tag="Buyer" />
            <rect x={380} y={22} width={340} height={160} rx={10} fill="var(--il-panel)" stroke="var(--il-line)" />
            <circle cx={398} cy={36} r={5} fill={eng.color} />
            <Text x={410} y={40}>{eng.name}</Text>
            <rect x={470} y={50} width={240} height={22} rx={8} fill="var(--il-tint)" />
            <Text x={702} y={65} anchor="end" weight={500}>{typed}</Text>
            {["Competitor Co", "Apex Industries", "Nova Works"].slice(0, answers).map((name, i) => (
              <g key={name}>
                <circle cx={398} cy={92 + i * 19} r={4} fill={GOOD} />
                <Text x={410} y={96 + i * 19} weight={500}>{`${i + 1}. ${name} · cited`}</Text>
              </g>
            ))}
            {missing && (
              <g>
                <rect x={392} y={150} width={168} height={20} rx={10} fill="var(--il-neg-tint)" stroke={NEGATIVE} />
                <Text x={476} y={164} anchor="middle" fill={NEGATIVE}>your brand · not listed</Text>
              </g>
            )}
            <Bubble x={200} y={96} vbW={W} text={missing ? "calling the top 3 now" : typed.length < q.length ? "asking AI for a shortlist" : "reading the answer"} />
          </g>
        );
      }}
    </Scene>
  );
}

/* 02 — the enquiries go to the rival that AI recommends */
export function StoryRivalWins() {
  return (
    <Scene step="02" spot={400} say="that should be you" end={6.8} title="Competitor gets the call" still={3.5} label="Enquiries fly from AI search to a competitor's desk while your desk stays empty">
      {(t) => {
        const { ph } = cycle(t, 8);
        const flights = [0, 1, 2].map((i) => clamp01((ph - 0.6 - i * 0.45) / 1.3));
        const landed = flights.filter((k) => k >= 1).length;
        return (
          <g>
            <circle cx={400} cy={40} r={18} fill="var(--il-panel)" stroke="var(--il-line)" />
            <Text x={400} y={44} anchor="middle" fill={ACCENT}>AI</Text>
            {flights.map((k, i) => {
              if (k <= 0 || k >= 1) return null;
              const x = (1 - k) * (1 - k) * 400 + 2 * (1 - k) * k * 300 + k * k * 215;
              const y = (1 - k) * (1 - k) * 58 + 2 * (1 - k) * k * 10 + k * k * 118;
              return (
                <g key={i}>
                  <rect x={x - 8} y={y - 5.5} width={16} height={11} rx={1.5} fill="var(--il-panel)" stroke="var(--il-mute)" />
                  <path d={`M${x - 8} ${y - 5} L${x} ${y + 1} L${x + 8} ${y - 5}`} fill="none" stroke="var(--il-mute)" />
                </g>
              );
            })}
            <Seated x={200} look={RIVAL_LOOK} t={t} seed={2} tag="Competitor Co" />
            <rect x={588} y={110} width={24} height={26} rx={6} fill="#CBD5E1" />
            <Desk x={600} sy={140} glow={false} />
            <Tag x={600} y={153} text="Your company" />
            <Bubble x={200} y={96} vbW={W} text={landed > 0 && ph < 7 ? `${landed} new enquir${landed > 1 ? "ies" : "y"}!` : "on the AI shortlist"} />
            <Bubble x={600} y={100} vbW={W} text="no calls this week" />
          </g>
        );
      }}
    </Scene>
  );
}

/* 03 — monitoring tools only watch; TopGEO's agents also build */
export function StoryWatchVsBuild() {
  return (
    <Scene step="03" spot={205} say="watching won't fix it" end={7.9} title="They watch. We build." still={5} label="A monitoring tool only reports a visibility drop, while TopGEO's Monitor Agent finds gaps and the Content Agent writes pages">
      {(t) => {
        const { n, ph } = cycle(t, 8);
        const page = n + 13;
        const fly = clamp01((ph - 2) / 1.2);
        const [wx, wy] = [150, 160];
        return (
          <g>
            <Text x={200} y={24} anchor="middle" fill={FAINT}>OTHER TOOLS · WATCH</Text>
            <Text x={600} y={24} anchor="middle" fill={ACCENT}>TOPGEO · WATCH + BUILD</Text>
            <line x1={400} y1={40} x2={400} y2={180} stroke="var(--il-line)" strokeDasharray="4 5" />
            <circle cx={400} cy={108} r={13} fill="var(--il-floor)" stroke="var(--il-line)" />
            <Text x={400} y={112} anchor="middle" fill={FAINT}>vs</Text>

            <rect x={230} y={50} width={130} height={90} rx={6} fill="var(--il-panel)" stroke="var(--il-line)" />
            <Text x={242} y={64} size={8} fill={FAINT}>visibility</Text>
            <polyline points="242,72 270,80 298,96 326,104 350,124" fill="none" stroke={NEGATIVE} strokeWidth={2} />
            <Rep at={[wx, wy]} l={RIVAL_LOOK} t={t} seed={3} />
            <g fill={TXT}>
              <circle cx={wx - 5} cy={wy - 34} r={4.2} />
              <circle cx={wx + 5} cy={wy - 34} r={4.2} />
              <rect x={wx - 2} y={wy - 36} width={4} height={3} />
            </g>
            <Bubble x={wx} y={wy - 52} vbW={W} text={ph < 4 ? "visibility down 4%" : "so what do we do now?"} />

            {fly > 0 && fly < 1 && <Paper at={[lerp(540, 680, fly), 118 - Math.sin(fly * Math.PI) * 30]} />}
            <Seated x={520} look={MONITOR.look} t={t} seed={4} tag="Monitor Agent" />
            <Seated x={700} look={CONTENT_LOOK} t={t} seed={5} tag="Content Agent" />
            <Bubble x={520} y={96} vbW={W} text={ph < 2 ? "gap found · 12 pages" : "brief sent to Content"} />
            <Bubble x={700} y={96} vbW={W} text={ph < 3.2 ? "ready for next brief" : ph < 6.5 ? `writing page #${page}` : `page #${page} is live`} />
          </g>
        );
      }}
    </Scene>
  );
}

/* 04 — SEE: every AI answer is read for mentions, rivals and citations */
const ANSWERS: [string, string][] = [
  ["cited", GOOD],
  ["missing", NEGATIVE],
  ["cited", GOOD],
  ["competitor cited", "#B45309"],
  ["missing", NEGATIVE],
  ["cited", GOOD],
];

export function StorySeeMentions() {
  return (
    <Scene step="04" spot={425} say="every answer, logged" end={7.9} title="We see every mention" still={7.5} label="The Execution Agent runs a buyer question on each AI engine and the Monitor Agent records where your brand is cited, missing, or beaten by a competitor">
      {(t) => {
        const { ph } = cycle(t, 8);
        const shown = Math.max(0, Math.min(6, Math.floor((ph - 0.4) / 0.9) + 1));
        const current = ENGINES[Math.max(0, Math.min(5, shown - 1))];
        return (
          <g>
            <Flow d="M178 122 L 300 122" t={t} />
            <Flow d="M368 122 C 400 122, 420 100, 448 100" t={t} />
            <Seated x={130} look={EXEC.look} t={t} seed={6} tag="Execution Agent" />
            <Seated x={320} look={MONITOR.look} t={t} seed={7} tag="Monitor Agent" />
            <rect x={450} y={20} width={320} height={162} rx={10} fill="var(--il-panel)" stroke="var(--il-line)" />
            <Text x={466} y={38} size={9} fill={FAINT}>AI ANSWERS · THIS RUN</Text>
            {ENGINES.slice(0, shown).map((e, i) => (
              <g key={e.name}>
                <circle cx={470} cy={54 + i * 18} r={4} fill={e.color} />
                <Text x={482} y={58 + i * 18} weight={500}>{e.name}</Text>
                <Text x={752} y={58 + i * 18} anchor="end" fill={ANSWERS[i][1]}>{ANSWERS[i][0]}</Text>
              </g>
            ))}
            <line x1={466} y1={162} x2={754} y2={162} stroke="var(--il-soft)" />
            <Text x={466} y={176}>GEO score</Text>
            <Text x={752} y={176} anchor="end" fill={ACCENT}>{ph > 6 ? "72 → 74" : "72"}</Text>
            <Bubble x={130} y={96} vbW={W} text={`running · ${current.name}`} />
            <Bubble x={320} y={96} vbW={W} text={MONITOR_LINES[Math.floor(t / 2) % MONITOR_LINES.length]} />
          </g>
        );
      }}
    </Scene>
  );
}

/* 06 — the Execution Agent works through every engine in turn */
export function StoryEveryEngine() {
  return (
    <Scene step="06" spot={755} say="one engine at a time" end={5.9} title="Every AI engine" still={3} from={0.75} span={0.75} label="The Execution Agent walks along the wall of AI engines and runs your buyer questions on each one">
      {(t) => {
        const { ph } = cycle(t, 12);
        const u = ph / 12;
        const x = 120 + (u < 0.5 ? u * 2 : (1 - u) * 2) * 560;
        // Each engine gets an equal share of the walk, so ChatGPT is lit for as long as the others.
        const active = Math.max(0, Math.min(5, Math.floor(((x - 120) / 560) * 6)));
        return (
          <g>
            {ENGINES.map((e, i) => (
              <g key={e.name}>
                <rect x={70 + i * 112} y={24} width={100} height={46} rx={5} fill="#2F333D" />
                <rect x={74 + i * 112} y={28} width={92} height={34} rx={3} fill={e.color} opacity={0.9} />
                <Text x={120 + i * 112} y={49} size={9.5} anchor="middle" fill="#FFFFFF">{e.name}</Text>
              </g>
            ))}
            <rect x={67 + active * 112} y={21} width={106} height={52} rx={7} fill="none" stroke={ACCENT} strokeWidth={3} />
            <Rep at={[x, 160]} l={EXEC.look} t={t} seed={8} walking stride={x / 7} />
            <Tag x={x} y={166} text="Execution Agent" />
            <Bubble x={x} y={108} vbW={W} text={`running · ${ENGINES[active].name}`} />
            <Text x={W / 2} y={14} size={9} anchor="middle" fill={FAINT}>25 QUESTIONS × 6 ENGINES</Text>
          </g>
        );
      }}
    </Scene>
  );
}

/* 07 — pages are built on the customer's own domain, on brand and checked */
export function StoryYourDomain() {
  return (
    <Scene step="07" spot={225} say="your domain, your brand" end={8.9} title="Live on your domain" still={7} label="The Site Audit Agent checks your site and the Brand Agent applies your brand while a new page builds on yourdomain.com">
      {(t) => {
        const { ph } = cycle(t, 9);
        const built = clamp01((ph - 0.5) / 4.5) * 6;
        const blocks: [number, number, number, number, string][] = [
          [266, 48, 204, 12, "#3B3F4A"],
          [266, 68, 254, 6, "var(--il-line)"],
          [266, 80, 234, 6, "var(--il-line)"],
          [266, 92, 244, 6, "var(--il-line)"],
          [266, 108, 184, 6, "var(--il-line)"],
        ];
        return (
          <g>
            <Flow d="M176 128 C 210 128, 220 100, 248 100" t={t} />
            <Flow d="M624 128 C 590 128, 580 100, 552 100" t={t} />
            <rect x={250} y={18} width={300} height={164} rx={8} fill="var(--il-panel)" stroke="var(--il-line)" />
            <path d="M250 26 a8 8 0 0 1 8 -8 h284 a8 8 0 0 1 8 8 v10 h-300 z" fill={ACCENT} />
            <Text x={400} y={31} size={8.5} anchor="middle" fill="#FFFFFF">yourdomain.com/cnc-suppliers</Text>
            {blocks.map(([bx, by, bw, bh, c], i) => built > i && <rect key={i} x={bx} y={by} width={bw} height={bh} rx={2} fill={c} />)}
            {built > 5 && (
              <g>
                <rect x={266} y={124} width={268} height={46} rx={4} fill="var(--il-tint)" stroke={ACCENT} />
                <rect x={440} y={150} width={84} height={14} rx={3} fill={ACCENT} />
                <Text x={482} y={160} size={8} anchor="middle" fill="#FFFFFF">Get a quote</Text>
              </g>
            )}
            {ph > 5.5 && (
              <g>
                <rect x={500} y={21} width={42} height={12} rx={6} fill="#22C55E" />
                <Text x={521} y={30} size={8} anchor="middle" fill="#FFFFFF">LIVE</Text>
              </g>
            )}
            <Rep at={[150, 160]} l={SITE_AUDIT.look} t={t} seed={9} />
            <Rep at={[650, 160]} l={BRAND.look} t={t} seed={10} />
            <Tag x={150} y={166} text="Site Audit Agent" />
            <Tag x={650} y={166} text="Brand Agent" />
            <Bubble x={150} y={108} vbW={W} text={AUDIT_LINES[Math.floor(t / 3) % AUDIT_LINES.length]} />
            <Bubble x={650} y={108} vbW={W} text={BRAND_LINES[Math.floor(t / 3) % BRAND_LINES.length]} />
          </g>
        );
      }}
    </Scene>
  );
}

/* 08 — human in the loop: you can approve each page before it ships */
export function StoryYouApprove() {
  return (
    <Scene step="08" spot={480} say="you stay in control" end={8.9} title="You approve" still={5} label="The QA Gate Agent brings a scored page to you; you approve it and it goes live on your website">
      {(t) => {
        const { n, ph } = cycle(t, 9);
        const page = n + 41;
        let qx = 110;
        let walking = false;
        let carrying = false;
        let qaText = "scoring next page";
        if (ph < 2) [qx, walking, carrying, qaText] = [lerp(110, 330, ph / 2), true, true, `> #${page} ready for your OK`];
        else if (ph < 2.6) [qx, carrying, qaText] = [330, true, `> #${page} ready for your OK`];
        else if (ph < 4.6) [qx, walking, qaText] = [lerp(330, 110, (ph - 2.6) / 2), true, "< heading back to desk"];
        const holding = ph >= 2.6 && ph < 6.2;
        const fly = clamp01((ph - 6.2) / 1);
        const live = ph >= 7.2;
        return (
          <g>
            <rect x={560} y={40} width={180} height={112} rx={8} fill="#2F333D" />
            <rect x={566} y={46} width={168} height={100} rx={5} fill="var(--il-panel)" />
            <rect x={566} y={46} width={168} height={12} rx={5} fill={ACCENT} />
            <Text x={650} y={55} size={7.5} anchor="middle" fill="#FFFFFF">yourdomain.com</Text>
            {[70, 82, 94].map((y) => (
              <rect key={y} x={578} y={y} width={y === 70 ? 120 : 140} height={6} rx={2} fill="var(--il-line)" />
            ))}
            {live && (
              <g>
                <rect x={590} y={112} width={120} height={20} rx={10} fill="#22C55E" />
                <Text x={650} y={126} anchor="middle" fill="#FFFFFF">{`page #${page} live`}</Text>
              </g>
            )}
            {fly > 0 && fly < 1 && <Paper at={[lerp(400, 620, fly), 135 - Math.sin(fly * Math.PI) * 40]} check />}
            <Rep at={[qx, 160]} l={QA_LOOK} t={t} seed={11} walking={walking} stride={qx / 7} carrying={carrying} />
            <Rep at={[400, 160]} l={HUMAN_LOOK} t={t} seed={12} carrying={holding} />
            {holding && ph >= 4.8 && <circle cx={406} cy={136} r={4.5} fill="#22C55E" />}
            <Tag x={qx} y={166} text="QA Gate Agent" />
            <Tag x={400} y={166} text="You · optional OK" />
            <Bubble x={qx} y={108} vbW={W} text={qaText} />
            {ph >= 3.4 && ph < 6.2 && <Bubble x={400} y={108} vbW={W} text={ph < 4.8 ? `reviewing page #${page}` : "approved · publish"} tone={ph < 4.8 ? "light" : "accent"} />}
          </g>
        );
      }}
    </Scene>
  );
}

/* 09 — DELIVER: the buyer from scene 01 sends an enquiry, tagged with its AI source */
export function StoryEnquiry() {
  return (
    <Scene step="09" spot={290} say="a real lead, with source" end={9.9} title="Enquiry lands" still={6.5} label="The buyer finds your page through AI search and sends an enquiry; the Lead Agent alerts you and names the AI engine that sent it">
      {(t) => {
        const { n, ph } = cycle(t, 10);
        const eng = ENGINES[n % ENGINES.length].name;
        const bx = ph < 2.4 ? lerp(60, 230, ph / 2.4) : 230;
        const sent = ph > 4.9;
        const alert = ph > 5.2;
        return (
          <g>
            <rect x={330} y={22} width={230} height={156} rx={8} fill="#2F333D" />
            <rect x={336} y={28} width={218} height={144} rx={5} fill="var(--il-panel)" />
            <rect x={336} y={28} width={218} height={12} rx={5} fill={ACCENT} />
            <Text x={445} y={37} size={7.5} anchor="middle" fill="#FFFFFF">yourdomain.com/cnc-suppliers</Text>
            {["Name", "Company", "Need"].map((label, i) => (
              <g key={label}>
                <Text x={350} y={58 + i * 28} size={8} fill={FAINT} weight={600}>{label}</Text>
                <rect x={350} y={62 + i * 28} width={190} height={13} rx={3} fill="var(--il-soft)" />
                <rect x={350} y={62 + i * 28} width={190 * clamp01((ph - 2.6 - i * 0.7) / 0.6)} height={13} rx={3} fill="var(--il-tint-2)" />
              </g>
            ))}
            <rect x={350} y={146} width={102} height={18} rx={4} fill={sent ? "#22C55E" : ACCENT} />
            <Text x={401} y={158} size={8.5} anchor="middle" fill="#FFFFFF">{sent ? "Enquiry sent" : "Send enquiry"}</Text>
            {alert && <Flow d="M562 100 C 590 100, 610 112, 628 120" t={t} />}
            <Rep at={[bx, 160]} l={BUYER_LOOK} t={t} seed={13} walking={ph < 2.4} stride={bx / 7} />
            <Rep at={[650, 160]} l={LEAD_LOOK} t={t} seed={14} />
            <Tag x={bx} y={166} text="Buyer" />
            <Tag x={650} y={166} text="Lead Agent" />
            <Bubble x={bx} y={108} vbW={W} text={ph < 2.4 ? `found you on ${eng}` : sent ? "enquiry sent!" : "need 500 CNC parts"} />
            <Bubble x={650} y={108} vbW={W} text={alert ? `alert sent · ${eng}` : "watching forms"} tone={alert ? "accent" : "light"} />
          </g>
        );
      }}
    </Scene>
  );
}
